import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { api } from "../lib/api";
import { useScope } from "../context/ScopeContext";
import { useAuth } from "../context/AuthContext";
import { PlanSummary } from "../components/PlanSummary";
import { useTheme } from "../context/ThemeContext";
import { useI18n } from "../i18n";
import { localizeName } from "../lib/catI18n";
import { Spinner, OverviewByScope } from "../lib/ui";
import { money, fmtDate, daysUntil } from "../lib/format";
import {
  ArrowRight, TrendingDown, Tag, Briefcase, CalendarClock, CopyCheck, FolderOpen, AlertCircle, ListChecks, Info,
} from "lucide-react";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function periodRange(kind, cf, ct) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const back = (days) => { const d = new Date(now); d.setDate(d.getDate() - days + 1); return d; };
  const shift = (d, days) => { const x = new Date(d); x.setDate(x.getDate() + days); return x; };
  if (kind === "all") return { from: null, to: null, pfrom: null, pto: null };
  if (kind === "last7" || kind === "last30" || kind === "last90") {
    const n = kind === "last7" ? 7 : kind === "last30" ? 30 : 90;
    const f = back(n);
    return { from: iso(f), to: iso(now), pfrom: iso(shift(f, -n)), pto: iso(shift(f, -1)) };
  }
  if (kind === "month") return { from: iso(new Date(y, m, 1)), to: iso(new Date(y, m + 1, 0)), pfrom: iso(new Date(y, m - 1, 1)), pto: iso(new Date(y, m, 0)) };
  if (kind === "lastMonth") return { from: iso(new Date(y, m - 1, 1)), to: iso(new Date(y, m, 0)), pfrom: iso(new Date(y, m - 2, 1)), pto: iso(new Date(y, m - 1, 0)) };
  if (kind === "quarter") { const q = Math.floor(m / 3); return { from: iso(new Date(y, q * 3, 1)), to: iso(new Date(y, q * 3 + 3, 0)), pfrom: iso(new Date(y, q * 3 - 3, 1)), pto: iso(new Date(y, q * 3, 0)) }; }
  if (kind === "lastQuarter") { const q = Math.floor(m / 3); return { from: iso(new Date(y, q * 3 - 3, 1)), to: iso(new Date(y, q * 3, 0)), pfrom: iso(new Date(y, q * 3 - 6, 1)), pto: iso(new Date(y, q * 3 - 3, 0)) }; }
  if (kind === "lastYear") return { from: iso(new Date(y - 1, 0, 1)), to: iso(new Date(y - 1, 11, 31)), pfrom: iso(new Date(y - 2, 0, 1)), pto: iso(new Date(y - 2, 11, 31)) };
  if (kind === "custom") return { from: cf || null, to: ct || null, pfrom: null, pto: null };
  return { from: iso(new Date(y, 0, 1)), to: iso(new Date(y, 11, 31)), pfrom: iso(new Date(y - 1, 0, 1)), pto: iso(new Date(y - 1, 11, 31)) };
}

// Sparkline SVG paths (viewBox 0 0 100 28) a partir de datos reales.
function sparkPaths(vals) {
  const v = (vals || []).map((x) => Number(x || 0));
  if (v.length < 2) return { line: "", area: "" };
  const max = Math.max(...v), min = Math.min(...v, 0);
  const rng = max - min || 1;
  const n = v.length;
  const pts = v.map((x, i) => [(i / (n - 1)) * 100, 25 - ((x - min) / rng) * 20]);
  const line = pts.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join("");
  const area = `${line}L100 28L0 28Z`;
  return { line, area };
}

function Spark({ vals, out }) {
  const { line, area } = sparkPaths(vals);
  if (!line) return null;
  return (
    <svg className={`spark${out ? "" : ""}`} viewBox="0 0 100 28" preserveAspectRatio="none">
      <path className="area" d={area} />
      <path d={line} />
    </svg>
  );
}

// Nota informativa (i) de la Visión general. Explica la suma Personal + Profesional.
function OverviewTip({ text }) {
  const [open, setOpen] = useState(false);
  return (
    <span style={{ position: "relative", display: "inline-flex", flex: "none" }}>
      <button type="button" data-testid="overview-tip-btn" aria-label="info"
        onClick={() => setOpen((o) => !o)} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}
        style={{ color: "var(--brass)", background: "none", border: 0, cursor: "pointer", lineHeight: 0, padding: 0 }}>
        <Info size={15} />
      </button>
      {open && (
        <span data-testid="overview-tip-popover" style={{ position: "absolute", top: "140%", left: 0, zIndex: 30, width: 280, background: "var(--surface, #fff)", border: "1px solid var(--line, #D5E7E5)", borderRadius: 8, padding: "10px 12px", fontSize: 12.5, lineHeight: 1.45, color: "var(--ink)", boxShadow: "0 10px 30px rgba(0,0,0,.14)" }}>{text}</span>
      )}
    </span>
  );
}

export default function Dashboard() {
  const { scope } = useScope();
  const { user, workspace } = useAuth();
  const { theme } = useTheme();
  const { t, locale, localeTag } = useI18n();
  const [period, setPeriod] = useState("year");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [cur, setCur] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("lura_alerts_dismissed") || "[]")); } catch { return new Set(); }
  });
  const dismiss = (id) => {
    setDismissed((prev) => { const n = new Set(prev); n.add(id); localStorage.setItem("lura_alerts_dismissed", JSON.stringify([...n])); return n; });
  };

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    const s = scope === "all" ? {} : { scope };
    const r = periodRange(period, customFrom, customTo);
    const dr = r.from ? { date_from: r.from, date_to: r.to } : {};
    try {
      const reqs = [
        api.get("/metrics/summary", { params: { ...s, ...dr } }),
        api.get("/metrics/charts", { params: { ...s, ...dr, ...(cur ? { currency: cur } : {}) } }),
        api.get("/transactions", { params: { ...s, ...dr, page: 1, page_size: 500 } }),
        api.get("/insights", { params: { ...s, ...dr } }),
        api.get("/alerts", { params: { ...s, ...dr, ...(r.pfrom ? { prev_from: r.pfrom, prev_to: r.pto } : {}), ...(cur ? { currency: cur } : {}) } }),
      ];
      reqs.push(r.pfrom
        ? api.get("/metrics/summary", { params: { ...s, date_from: r.pfrom, date_to: r.pto } })
        : Promise.resolve({ data: null }));
      // Visión general: pedimos el resumen por ámbito (Personal y Profesional) para el bloque Personal | Profesional | Total.
      if (scope === "all") {
        reqs.push(api.get("/metrics/summary", { params: { scope: "personal", ...dr } }));
        reqs.push(api.get("/metrics/summary", { params: { scope: "professional", ...dr } }));
      }
      // Resiliente: los datos núcleo (resumen, gráficos, movimientos) son obligatorios; los paneles
      // secundarios (insights, alertas, periodo anterior) son opcionales y no bloquean el panel.
      const res = await Promise.allSettled(reqs);
      const [sm, ch, tx, ins, al, prev] = res;
      if ([sm, ch, tx].some((x) => x.status !== "fulfilled")) { setError(true); return; }
      const ovP = res[6], ovPro = res[7];
      setData({
        summary: sm.value.data, charts: ch.value.data, items: tx.value.data.items, total: tx.value.data.total,
        insights: ins.status === "fulfilled" ? ins.value.data : [],
        alerts: al.status === "fulfilled" ? al.value.data : [],
        prev: prev.status === "fulfilled" ? prev.value.data : null,
        ovPersonal: ovP && ovP.status === "fulfilled" ? ovP.value.data : null,
        ovPro: ovPro && ovPro.status === "fulfilled" ? ovPro.value.data : null,
      });
    } catch (e) { setError(true); } finally { setLoading(false); }
  }, [scope, period, cur, customFrom, customTo]);

  useEffect(() => { load(); }, [load]);
  const [acts, setActs] = useState([]);
  useEffect(() => { api.get("/activities").then((r) => setActs(r.data)).catch(() => {}); }, []);
  const [commit, setCommit] = useState({});
  useEffect(() => { const s = scope === "all" ? {} : { scope }; api.get("/metrics/commitments", { params: s }).then((r) => setCommit(r.data || {})).catch(() => setCommit({})); }, [scope]);

  const base = data?.summary?.base_currency || "EUR";
  const currency = cur || base;
  const currencies = useMemo(() => (data ? Object.keys(data.summary.by_currency || {}) : []), [data]);

  if (loading || !data) return <Spinner />;
  if (error) return <div className="py-16 text-center text-sm" style={{ color: "var(--neg)" }} data-testid="dashboard-error">{t("dash.loadError")}</div>;

  const totals = data.summary.by_currency[currency] || { income: 0, expense: 0, result: 0, outflow: 0, debt: 0, investment: 0, transfer: 0 };
  const outflow = Number(totals.outflow ?? (Number(totals.expense) + Number(totals.investment || 0) + Number(totals.debt || 0)));
  const saved = Number(totals.saved || 0);
  const prevTotals = data.prev ? (data.prev.by_currency[currency] || null) : null;
  const upcoming = data.summary.upcoming_invoices || [];
  const items = data.items || [];
  // Visión general: bloque Personal | Profesional | Total (por moneda, sin convertir).
  const ovShow = scope === "all" && data.ovPersonal && data.ovPro;

  const topExp = (data.charts.expense_by_category || [])[0];
  const insights = [];
  if (prevTotals && Number(totals.result) < Number(prevTotals.result)) insights.push({ icon: TrendingDown, title: t("dash.resultDown"), value: money(Number(totals.result) - Number(prevTotals.result), currency), to: "/charts" });
  if (topExp) insights.push({ icon: Tag, title: t("dash.topExpense"), sub: localizeName(topExp.name, locale), value: money(topExp.value, currency), to: "/charts" });
  const topInc = (data.charts.income_by_service || [])[0];
  if (topInc) insights.push({ icon: Briefcase, title: t("dash.topIncome"), sub: localizeName(topInc.name, locale), value: money(topInc.value, currency), to: "/charts" });
  const nextInv = upcoming[0];
  if (nextInv) { const d = daysUntil(nextInv.due_date); insights.push({ icon: CalendarClock, title: nextInv.counterparty, sub: nextInv.number, value: (d != null && d < 0) ? t("dash.overdue") : `${d} d`, to: "/contracts" }); }
  const dupCount = items.filter((i) => i.possible_duplicate).length;
  if (dupCount) insights.push({ icon: CopyCheck, title: t("dash.duplicates"), value: String(dupCount), to: "/movements" });
  const uncatCount = items.filter((i) => !i.category_id).length;
  if (uncatCount) insights.push({ icon: FolderOpen, title: t("dash.uncategorized"), value: String(uncatCount), to: "/movements" });
  const topInsights = insights.slice(0, 4);

  const noAccount = items.filter((i) => !i.account_id).length;
  const reviews = [
    { label: t("dash.uncategorized"), count: uncatCount, to: "/movements" },
    { label: t("dash.noAccount"), count: noAccount, to: "/movements" },
    { label: t("dash.duplicates"), count: dupCount, to: "/movements" },
  ].filter((r) => r.count > 0);

  const catItems = (data.charts.expense_by_category || []).map((c) => ({ label: localizeName(c.name, locale), value: Number(c.value) }));
  const fv = data.charts.fixed_vs_variable || [];
  const fixedVal = (fv.find((x) => x.name === "Fijos") || {}).value || 0;
  const variableVal = (fv.find((x) => x.name === "Variables") || {}).value || 0;
  const svcItems = (data.charts.income_by_service || []).map((s) => ({ label: localizeName(s.name, locale), value: Number(s.value) }));
  const pl = (data.charts.profit_loss || []);
  const recent = items.slice(0, 6);
  const empty = items.length === 0;
  const rr = periodRange(period, customFrom, customTo);
  const rangeLabel = rr.from ? `${rr.from} → ${rr.to}` : t("dash.pAll");
  const insData = data.insights || {};
  const fc = (insData.forecast || []).find((f) => f.currency === currency) || null;
  const antC = (insData.ant_expenses || []).find((a) => a.currency === currency) || null;
  const leaksCur = (insData.leaks || []).filter((l) => l.currency === currency);
  const allAlerts = (data.alerts?.alerts) || [];
  const visibleAlerts = allAlerts.filter((a) => !dismissed.has(a.id));
  const alertMsg = (a) => t(`alert.${a.type}`)
    .replace("{amount}", a.amount ? money(a.amount, a.currency) : "")
    .replace("{count}", a.count).replace("{pct}", a.count)
    .replace("{category}", a.category ? localizeName(a.category, locale) : "");

  const fmtNum = (v) => new Intl.NumberFormat(localeTag || "es", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v || 0));
  const sign = (v) => (Number(v) < 0 ? "−" : "+");
  const incSeries = pl.map((p) => Number(p.income || 0));
  const expSeries = pl.map((p) => Number(p.expense || 0));
  const incomeBase = Number(totals.income) || 1;
  const wfW = (v) => `${Math.max(0, Math.min(100, (Math.abs(Number(v)) / incomeBase) * 100))}%`;
  const palette = theme === "dark"
    ? { pine: "#86BBAC", brass: "#C9A66B", ink: "#E8ECE7", line: "#232E2B", muted: "#9AA6A2", surface: "#111917" }
    : { pine: "#17423B", brass: "#8E6F3E", ink: "#0E1B19", line: "#D9DDD6", muted: "#5A6663", surface: "#FAFAF8" };
  const c6 = ["var(--c1)", "var(--c2)", "var(--c3)", "var(--c4)", "var(--c5)", "var(--c6)"];
  const catMax = Math.max(...catItems.map((c) => c.value), 1);
  const CM = {
    es: { title: "Compromisos futuros", sub: "Pagos previstos aún no asociados", d30: "30 días", d60: "60 días", d90: "90 días", total: "Total", personal: t("scope.personal"), professional: t("scope.professional"), tot: "Total", none: "Sin compromisos próximos" },
    en: { title: "Upcoming commitments", sub: "Planned payments not yet linked", d30: "30 days", d60: "60 days", d90: "90 days", total: "Total", personal: t("scope.personal"), professional: t("scope.professional"), tot: "Total", none: "No upcoming commitments" },
    fr: { title: "Engagements futurs", sub: "Paiements prévus non encore associés", d30: "30 jours", d60: "60 jours", d90: "90 jours", total: "Total", personal: t("scope.personal"), professional: t("scope.professional"), tot: "Total", none: "Aucun engagement à venir" },
    pt: { title: "Compromissos futuros", sub: "Pagamentos previstos ainda não associados", d30: "30 dias", d60: "60 dias", d90: "90 dias", total: "Total", personal: t("scope.personal"), professional: t("scope.professional"), tot: "Total", none: "Sem compromissos próximos" },
    it: { title: "Impegni futuri", sub: "Pagamenti previsti non ancora associati", d30: "30 giorni", d60: "60 giorni", d90: "90 giorni", total: "Total", personal: t("scope.personal"), professional: t("scope.professional"), tot: "Totale", none: "Nessun impegno in arrivo" },
  };
  const CMX = CM[locale] || CM.es;
  const commitCurs = Object.keys(commit || {});
  const initials = (s) => (s || "—").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "—";
  const inVsOutLabel = { es: "Entradas y salidas por mes", en: "Income and expenses by month", fr: "Entrées et sorties par mois", pt: "Entradas e saídas por mês", it: "Entrate e uscite per mese" }[locale] || "Entradas y salidas por mes";
  const BYACT = ({
    es: { title: "Por actividad", activity: "Actividad", inflows: "Entradas", outflows: "Salidas", result: "Resultado", none: "General / sin actividad" },
    en: { title: "By activity", activity: "Activity", inflows: "Inflows", outflows: "Outflows", result: "Result", none: "General / no activity" },
    fr: { title: "Par activité", activity: "Activité", inflows: "Entrées", outflows: "Sorties", result: "Résultat", none: "Général / sans activité" },
    pt: { title: "Por atividade", activity: "Atividade", inflows: "Entradas", outflows: "Saídas", result: "Resultado", none: "Geral / sem atividade" },
    it: { title: "Per attività", activity: "Attività", inflows: "Entrate", outflows: "Uscite", result: "Risultato", none: "Generale / senza attività" },
  })[locale] || { title: "Por actividad", activity: "Actividad", inflows: "Entradas", outflows: "Salidas", result: "Resultado", none: "General / sin actividad" };
  const actAgg = (() => {
    if (scope !== "professional" && scope !== "personal") return null;
    const map = {};
    items.forEach((it) => {
      if ((it.currency_code || "EUR") !== currency) return;
      const key = it.activity_id || "__none__";
      const m = (map[key] = map[key] || { income: 0, outflow: 0 });
      const amt = Number(it.amount || 0);
      if (it.type === "income") m.income += amt;
      else if (["expense", "investment", "debt"].includes(it.type)) m.outflow += amt;
    });
    const rows = Object.entries(map).map(([k, m]) => ({ id: k === "__none__" ? null : k, name: k === "__none__" ? null : (acts.find((a) => a.id === k)?.name || "—"), income: m.income, expense: m.outflow, result: m.income - m.outflow }));
    rows.sort((a, b) => b.result - a.result);
    return rows;
  })();

  return (
    <section className="view" data-testid="dashboard-panel">
      <div className="page-h">
        <div>
          <h1>{t("nav.panel")}</h1>
          <p>{t("dash.workspace")}: {workspace?.name || "—"}</p>
        </div>
        <div className="acts">
          <label className="period">
            <select data-testid="dashboard-period" value={period} onChange={(e) => setPeriod(e.target.value)} className="sel">
              <option value="last7">{t("dash.pLast7")}</option>
              <option value="last30">{t("dash.pLast30")}</option>
              <option value="last90">{t("dash.pLast90")}</option>
              <option value="month">{t("dash.pThisMonth")}</option>
              <option value="lastMonth">{t("dash.pLastMonth")}</option>
              <option value="quarter">{t("dash.pThisQuarter")}</option>
              <option value="lastQuarter">{t("dash.pLastQuarter")}</option>
              <option value="year">{t("dash.pThisYear")}</option>
              <option value="lastYear">{t("dash.pLastYear")}</option>
              <option value="all">{t("dash.pAll")}</option>
              <option value="custom">{t("dash.pCustom")}</option>
            </select>
          </label>
          {period === "custom" && (
            <>
              <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="sel" data-testid="dashboard-custom-from" />
              <span style={{ color: "var(--muted)" }}>→</span>
              <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="sel" data-testid="dashboard-custom-to" />
            </>
          )}
          {currencies.length > 1 && (
            <select data-testid="dashboard-currency" value={currency} onChange={(e) => setCur(e.target.value)} className="sel">
              {currencies.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          <Link to="/charts" className="btn primary" data-testid="dashboard-add-movement">
            {t("dash.cta")} <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
          </Link>
        </div>
      </div>

      <p className="eyebrow" style={{ marginTop: -16, marginBottom: 20 }} data-testid="dashboard-period-range">{t("dash.pShowing")}: {rangeLabel}</p>

      {user?.role !== "accountant" && <PlanSummary />}

      {scope === "all" && (
        <div data-testid="overview-note" style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: -8, marginBottom: 18, color: "var(--muted)", fontSize: 13.5, maxWidth: 760 }}>
          <span>{t("ov.explain")}</span>
          <OverviewTip text={t("ov.tip")} />
        </div>
      )}

      {ovShow && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-h"><h2>{t("ov.byScope")}</h2></div>
          <div className="card-b"><OverviewByScope personal={data.ovPersonal} pro={data.ovPro} /></div>
        </div>
      )}

      {empty ? (
        <div className="card" data-testid="dashboard-empty">
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", padding: "64px 24px" }}>
            <ListChecks size={26} style={{ color: "var(--muted)" }} />
            <p style={{ fontFamily: "var(--f-display)", fontSize: 22, marginTop: 14, color: "var(--ink)" }}>{t("dash.emptyPeriod")}</p>
            <p style={{ color: "var(--muted)", marginTop: 6, fontSize: 13.5 }}>{t("dash.emptyPeriodSub")}</p>
            <Link to="/movements" className="btn primary" style={{ marginTop: 16 }}>{t("dash.cta")}</Link>
          </div>
        </div>
      ) : (
        <>
          {/* Resultado + KPIs */}
          <div className="hero-kpi">
            <div className="free" data-testid="kpi-card-result">
              <div className="eyebrow">{t("kpi.result")}</div>
              <div className="big">{sign(totals.result)}{fmtNum(Math.abs(totals.result))}<small>{currency}</small></div>
              <p className="sub">{t("dash.resultFormula")}</p>
              <div className="waterfall">
                <div className="wf in"><span className="k">{t("kpi.income")}</span><span className="t"><i style={{ width: "100%" }} /></span><span className="v">+{fmtNum(totals.income)} {currency}</span></div>
                <div className="wf out"><span className="k">{t("dash.fixed")}</span><span className="t"><i style={{ width: wfW(fixedVal) }} /></span><span className="v">−{fmtNum(fixedVal)} {currency}</span></div>
                <div className="wf out"><span className="k">{t("dash.variable")}</span><span className="t"><i style={{ width: wfW(variableVal) }} /></span><span className="v">−{fmtNum(variableVal)} {currency}</span></div>
                {Number(totals.investment) > 0 && <div className="wf out"><span className="k">{t("type.investment")}</span><span className="t"><i style={{ width: wfW(Number(totals.investment)) }} /></span><span className="v">−{fmtNum(totals.investment)} {currency}</span></div>}
                {Number(totals.debt) > 0 && <div className="wf out"><span className="k">{t("type.debt")}</span><span className="t"><i style={{ width: wfW(Number(totals.debt)) }} /></span><span className="v">−{fmtNum(totals.debt)} {currency}</span></div>}
                <div className="wf res"><span className="k">{t("kpi.result")}</span><span className="t"><i style={{ width: wfW(totals.result) }} /></span><span className="v">{sign(totals.result)}{fmtNum(Math.abs(totals.result))} {currency}</span></div>
              </div>
            </div>
            <div className="kgrid">
              <div className="card kpi" data-testid="kpi-card-income"><span className="eyebrow">{t("kpi.income")}</span><div className="v">{fmtNum(totals.income)}<small>{currency}</small></div><div className="d">{t("dash.pShowing")}</div><Spark vals={incSeries} /></div>
              <div className="card kpi out" data-testid="kpi-card-expenses"><span className="eyebrow">{t("kpi.expense")}</span><div className="v">{fmtNum(outflow)}<small>{currency}</small></div><div className="d">{t("dash.pShowing")}</div><Spark vals={expSeries} out /></div>
              {saved > 0 && <div className="card kpi" data-testid="kpi-card-saved"><span className="eyebrow">{t("type.savings")}</span><div className="v">{fmtNum(saved)}<small>{currency}</small></div><div className="d">{t("dash.otherFlowsHint")}</div></div>}
              <div className="card kpi out" data-testid="kpi-fixed"><span className="eyebrow">{t("dash.fixed")}</span><div className="v">{fmtNum(fixedVal)}<small>{currency}</small></div><div className="d">{t("dash.fixed")}</div><Spark vals={expSeries} out /></div>
              <div className="card kpi out" data-testid="kpi-variable"><span className="eyebrow">{t("dash.variable")}</span><div className="v">{fmtNum(variableVal)}<small>{currency}</small></div><div className="d">{t("dash.variable")}</div><Spark vals={expSeries} out /></div>
            </div>
          </div>

          {/* Gráfico + próximos pagos */}
          <div className="row2">
            <div className="card" data-testid="dashboard-evolution">
              <div className="card-h">
                <h2>{inVsOutLabel}</h2>
                <div className="legend">
                  <span><i style={{ background: palette.pine }} />{t("chart.income")}</span>
                  <span><i style={{ background: palette.brass, opacity: .7 }} />{t("chart.expense")}</span>
                  <span><i style={{ background: palette.ink, height: 2 }} />{t("chart.result")}</span>
                </div>
              </div>
              <div className="card-b" style={{ paddingTop: 8 }}>
                {pl.length < 1 ? (
                  <p style={{ color: "var(--muted)", padding: "32px 0", textAlign: "center", fontSize: 13.5 }}>{t("dash.noComparison")}</p>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <ComposedChart data={pl} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid vertical={false} stroke={palette.line} />
                      <XAxis dataKey="period" tick={{ fontSize: 10, fill: palette.muted }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: palette.muted }} width={48} axisLine={false} tickLine={false} />
                      <Tooltip formatter={(v) => money(v, currency)} contentStyle={{ borderRadius: 6, border: `1px solid ${palette.line}`, fontSize: 12, background: palette.surface, color: palette.ink }} />
                      <Bar dataKey="income" name={t("chart.income")} fill={palette.pine} radius={[2, 2, 0, 0]} maxBarSize={18} />
                      <Bar dataKey="expense" name={t("chart.expense")} fill={palette.brass} fillOpacity={0.6} radius={[2, 2, 0, 0]} maxBarSize={18} />
                      <Line type="monotone" dataKey="result" name={t("chart.result")} stroke={palette.ink} strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
            <div className="card" data-testid="kpi-card-upcoming">
              <div className="card-h"><h2>{t("dash.upcoming")}</h2><Link to="/contracts" className="btn sm">{t("dash.viewAll")}</Link></div>
              <div className="card-b">
                {upcoming.length === 0 ? (
                  <div style={{ padding: "8px 0" }}>
                    <p style={{ color: "var(--muted)", fontSize: 13.5 }}>{t("dash.noUpcoming")}</p>
                    <Link to="/contracts" data-testid="dashboard-add-invoice" className="btn sm" style={{ marginTop: 10 }}>{t("dash.addInvoice")}</Link>
                  </div>
                ) : (
                  <div className="oblig">
                    {upcoming.slice(0, 5).map((u, i) => {
                      const d = new Date(u.due_date);
                      const mon = d.toLocaleDateString(localeTag || "es", { month: "short" }).replace(".", "");
                      return (
                        <div className="ob" key={i}>
                          <div className="date"><b>{String(d.getDate()).padStart(2, "0")}</b><small>{mon}</small></div>
                          <div className="t"><b>{u.counterparty}</b><small><span className={`chip ${u.scope === "professional" ? "pro" : "personal"}`}>{u.scope === "professional" ? t("scope.professional") : t("scope.personal")}</span></small></div>
                          <div className="a">−{fmtNum(u.amount)} {u.currency_code}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Compromisos futuros (pagos previstos de contratos, por ámbito y moneda) */}
          {commitCurs.length > 0 && (
            <div className="card" data-testid="dashboard-commitments" style={{ marginTop: 20 }}>
              <div className="card-h"><h2>{CMX.title}</h2><Link to="/contracts" className="btn sm">{t("dash.viewAll")}</Link></div>
              <div className="card-b">
                <p className="eyebrow" style={{ marginBottom: 12 }}>{CMX.sub}</p>
                {commitCurs.map((curc) => {
                  const v = commit[curc]; const p = v.personal, pr = v.professional;
                  const row = (k) => ({ p: p[k] || 0, pr: pr[k] || 0, t: (p[k] || 0) + (pr[k] || 0) });
                  const bands = [["d30", CMX.d30], ["d60", CMX.d60], ["d90", CMX.d90], ["total", CMX.total]];
                  return (
                    <div key={curc} style={{ marginBottom: 14 }} data-testid={`commitments-${curc}`}>
                      <div style={{ fontWeight: 700, fontSize: 12, color: "var(--muted)", marginBottom: 6 }}>{curc}</div>
                      <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                          <thead><tr style={{ textAlign: "right", color: "var(--muted)", fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em" }}>
                            <th style={{ textAlign: "left", padding: "4px 8px" }}></th><th style={{ padding: "4px 8px" }}>{CMX.personal}</th><th style={{ padding: "4px 8px" }}>{CMX.professional}</th><th style={{ padding: "4px 8px" }}>{CMX.tot}</th>
                          </tr></thead>
                          <tbody>
                            {bands.map(([k, lbl]) => { const r = row(k); return (
                              <tr key={k} style={{ textAlign: "right", borderTop: "1px solid var(--line)", fontWeight: k === "total" ? 700 : 400 }}>
                                <td style={{ textAlign: "left", padding: "5px 8px", color: "var(--ink)" }}>{lbl}</td>
                                <td style={{ padding: "5px 8px", fontVariantNumeric: "tabular-nums" }}>{fmtNum(r.p)}</td>
                                <td style={{ padding: "5px 8px", fontVariantNumeric: "tabular-nums" }}>{fmtNum(r.pr)}</td>
                                <td style={{ padding: "5px 8px", fontVariantNumeric: "tabular-nums", color: "var(--ink)" }}>{fmtNum(r.t)}</td>
                              </tr>
                            ); })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Categorías + últimos movimientos */}
          <div className="row2">
            <div className="card" data-testid="dashboard-by-category">
              <div className="card-h"><h2>{t("dash.byCategory")}</h2></div>
              <div className="card-b">
                {catItems.length === 0 ? <p style={{ color: "var(--muted)", padding: "24px 0", textAlign: "center", fontSize: 13.5 }}>—</p> : (
                  <div className="cats" data-testid="breakdown-category">
                    {catItems.slice(0, 6).map((c, i) => (
                      <div className="cat-row" key={i}>
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.label}</span>
                        <span className="bar"><i style={{ background: c6[i % 6], width: `${(c.value / catMax) * 100}%` }} /></span>
                        <span className="v">{fmtNum(c.value)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="card" data-testid="dashboard-recent">
              <div className="card-h"><h2>{t("dash.recent")}</h2><Link to="/movements" className="btn sm">{t("dash.viewAll")}</Link></div>
              <div className="card-b" style={{ paddingTop: 8 }}>
                <div className="tbl-wrap">
                  <table className="mini-t">
                    <tbody>
                      {recent.map((tx) => (
                        <tr key={tx.id}>
                          <td>
                            <div className="merchant">
                              <span className="lg">{initials(tx.description)}</span>
                              <div className="nm"><b>{tx.description || "—"}</b><small>{fmtDate(tx.date)}</small></div>
                            </div>
                          </td>
                          <td className={`amt ${tx.type === "income" ? "pos" : ""}`}>{tx.type === "income" ? "+" : "−"}{fmtNum(tx.amount)} {tx.currency_code}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Secciones reales conservadas */}
          <div className="stack">
            <div className="card" data-testid="dashboard-vat">
              <div className="card-b" style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13.5 }}>
                <AlertCircle size={16} style={{ color: "var(--brass)", marginTop: 2, flex: "none" }} />
                <div>
                  <span data-testid="dashboard-tax-estimate">{t("dash.taxEstimate")}: <b>{data.summary.vat.rate_percent}%</b> · {t("kpi.vat")} {data.summary.vat.label}: <b>{money(data.summary.vat.estimate, base)}</b>. {t("dash.vatOrient")}</span>
                  <Link to="/settings" data-testid="dashboard-configure-regional" className="btn sm" style={{ marginTop: 10 }}>{t("dash.configureRegional")}</Link>
                </div>
              </div>
            </div>

            {actAgg && actAgg.length > 0 && (
              <div className="card" data-testid="dashboard-by-activity">
                <div className="card-h"><h2>{BYACT.title}</h2></div>
                <div className="card-b" style={{ overflowX: "auto" }}>
                  <table className="w-full text-sm">
                    <thead><tr style={{ textAlign: "left", color: "var(--muted)", fontSize: 12 }}>
                      <th style={{ padding: "6px 8px" }}>{BYACT.activity}</th>
                      <th style={{ padding: "6px 8px", textAlign: "right" }}>{BYACT.inflows}</th>
                      <th style={{ padding: "6px 8px", textAlign: "right" }}>{BYACT.outflows}</th>
                      <th style={{ padding: "6px 8px", textAlign: "right" }}>{BYACT.result}</th>
                    </tr></thead>
                    <tbody>
                      {actAgg.map((r, i) => (
                        <tr key={i} data-testid={`by-activity-row-${r.id || "none"}`} style={{ borderTop: "1px solid var(--line)" }}>
                          <td style={{ padding: "8px" }}><Link to={r.id ? `/movements?activity=${r.id}` : "/movements?activity=none"} style={{ color: "var(--pine)", fontWeight: 500 }} data-testid={`by-activity-link-${r.id || "none"}`}>{r.name || BYACT.none}</Link></td>
                          <td className="num" style={{ padding: "8px", textAlign: "right", color: "var(--pine)" }}>{money(r.income, currency)}</td>
                          <td className="num" style={{ padding: "8px", textAlign: "right" }}>{money(r.expense, currency)}</td>
                          <td className="num" style={{ padding: "8px", textAlign: "right", color: r.result < 0 ? "var(--neg)" : "var(--pine)", fontWeight: 600 }}>{money(r.result, currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {topInsights.length > 0 && (
              <div className="card" data-testid="dashboard-attention">
                <div className="card-h"><h2>{t("dash.attention")}</h2></div>
                <div className="card-b"><div className="kgrid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))" }}>
                  {topInsights.map((ins, i) => (
                    <Link key={i} to={ins.to} data-testid={`insight-${i}`} className="card" style={{ padding: 16, textDecoration: "none" }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", color: "var(--brass)", marginBottom: 8 }}><ins.icon size={16} /><span className="eyebrow">{t("dash.review")}</span></div>
                      <p style={{ fontWeight: 500, color: "var(--ink)", fontSize: 13.5 }}>{ins.title}</p>
                      {ins.sub && <p style={{ color: "var(--muted)", fontSize: 12, marginTop: 2 }}>{ins.sub}</p>}
                      <p className="num" style={{ fontSize: 18, color: "var(--pine)", marginTop: 4 }}>{ins.value}</p>
                    </Link>
                  ))}
                </div></div>
              </div>
            )}

            {visibleAlerts.length > 0 && (
              <div className="card" data-testid="dashboard-alerts">
                <div className="card-h"><h2>{t("dash.alertsTitle")}</h2></div>
                <div className="card-b" style={{ display: "grid", gap: 8 }}>
                  {visibleAlerts.map((a) => (
                    <div key={a.id} data-testid={`alert-${a.id}`} className="card" style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "12px 14px", alignItems: "flex-start" }}>
                      <div style={{ display: "flex", gap: 8, fontSize: 13.5 }}><AlertCircle size={16} style={{ color: "var(--brass)", marginTop: 2, flex: "none" }} /><span>{alertMsg(a)} <span style={{ color: "var(--muted)" }}>· {rangeLabel}</span></span></div>
                      <div style={{ display: "flex", gap: 12, flex: "none" }}>
                        <Link to={a.type === "duplicates" ? "/movements?review=duplicates" : "/movements"} style={{ color: "var(--pine)", fontWeight: 500, fontSize: 13 }} data-testid={`alert-review-${a.id}`}>{t("alert.review")}</Link>
                        <button onClick={() => dismiss(a.id)} style={{ color: "var(--muted)", fontSize: 13, background: "none", border: 0 }} data-testid={`alert-ignore-${a.id}`}>{t("alert.ignore")}</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="card" data-testid="dashboard-insights">
              <div className="card-h"><h2>{t("dash.insightsTitle")}</h2><span className="chip pro">Premium</span></div>
              <div className="card-b"><div className="kgrid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))" }}>
                <div className="card" style={{ padding: 16 }} data-testid="insight-forecast">
                  <p className="eyebrow" style={{ marginBottom: 6 }}>{t("dash.forecast")}</p>
                  {fc && fc.complete ? (<><p className="num" style={{ fontSize: 18, color: Number(fc.projected) < 0 ? "var(--neg)" : "var(--pine)" }} data-testid="forecast-amount">{money(fc.projected, currency)}</p><p style={{ color: "var(--muted)", fontSize: 12, marginTop: 4 }}>{t("dash.forecastHint")} · {money(fc.upcoming, currency)}</p></>) : <p style={{ color: "var(--muted)", fontSize: 13.5 }} data-testid="forecast-incomplete">{t("dash.forecastIncomplete")}</p>}
                </div>
                <div className="card" style={{ padding: 16 }} data-testid="insight-ant">
                  <p className="eyebrow" style={{ marginBottom: 6 }}>{t("dash.antExpenses")}</p>
                  {antC ? (<><p className="num" style={{ fontSize: 18, color: "var(--ink)" }}>{money(antC.total, currency)}</p><p style={{ color: "var(--muted)", fontSize: 12, marginTop: 4 }}>{antC.count} {t("dash.antHint")}</p></>) : <p style={{ color: "var(--muted)", fontSize: 13.5 }}>{t("dash.antNone")}</p>}
                </div>
                <div className="card" style={{ padding: 16 }} data-testid="insight-leaks">
                  <p className="eyebrow" style={{ marginBottom: 6 }}>{t("dash.leaks")}</p>
                  {leaksCur.length ? (<div style={{ display: "grid", gap: 4, marginTop: 4 }}>{leaksCur.slice(0, 3).map((l, i) => (<div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}><span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginRight: 8 }}>{l.label}</span><span className="num">{money(l.total, currency)} · {l.occurrences}×</span></div>))}</div>) : <p style={{ color: "var(--muted)", fontSize: 13.5 }}>{t("dash.leaksNone")}</p>}
                </div>
              </div></div>
            </div>

            {svcItems.length > 0 && (
              <div className="card" data-testid="dashboard-by-service">
                <div className="card-h"><h2>{t("dash.byService")}</h2></div>
                <div className="card-b"><div className="cats" data-testid="breakdown-service">
                  {svcItems.slice(0, 6).map((s, i) => {
                    const mx = Math.max(...svcItems.map((x) => x.value), 1);
                    return (<div className="cat-row" key={i}><span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.label}</span><span className="bar"><i style={{ background: c6[i % 6], width: `${(s.value / mx) * 100}%` }} /></span><span className="v">{fmtNum(s.value)}</span></div>);
                  })}
                </div></div>
              </div>
            )}

            {reviews.length > 0 && (
              <div className="card" data-testid="dashboard-review">
                <div className="card-h"><h2>{t("dash.toReview")}</h2></div>
                <div className="card-b" style={{ display: "grid", gap: 8 }}>
                  {reviews.map((r, i) => (
                    <Link key={i} to={r.to} data-testid={`review-${i}`} className="card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", textDecoration: "none" }}>
                      <span style={{ fontSize: 13.5, color: "var(--ink)" }}>{r.label}</span>
                      <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}><b className="num" style={{ color: "var(--pine)" }}>{r.count}</b><ArrowRight size={14} style={{ color: "var(--muted)" }} /></span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}
